import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Box, Card, Table, Typography } from '@mui/joy';
import {
  BarChart2,
  CheckCircle2,
  Clock3,
  PieChart as PieIcon,
  Table2,
  TrendingUp,
  Users,
  XCircle,
} from 'lucide-react';
import './KpiWidget.css';

const CHART_COLORS = ['#204385', '#3b82f6', '#0f172a', '#ef4444', '#94a3b8', '#cbd5e1'];
const safeSeries = (value) => (Array.isArray(value) ? value : []);

const chartIconByType = {
  CARD: TrendingUp,
  BAR: BarChart2,
  LINE: TrendingUp,
  PIE: PieIcon,
  TABLE: Table2,
};

const compactTones = {
  blue: {
    value: '#2563eb',
    badgeBg: '#eff6ff',
    badgeFg: '#2563eb',
  },
  green: {
    value: '#10b981',
    badgeBg: '#ecfdf5',
    badgeFg: '#10b981',
  },
  red: {
    value: '#ef4444',
    badgeBg: '#fef2f2',
    badgeFg: '#ef4444',
  },
  orange: {
    value: '#f97316',
    badgeBg: '#fff7ed',
    badgeFg: '#f97316',
  },
  default: {
    value: '#204385',
    badgeBg: '#eff6ff',
    badgeFg: '#204385',
  },
};

const keywordConfig = [
  {
    tone: 'blue',
    Icon: Clock3,
    matches: ['EN_COURS', 'PENDING', 'IN_PROGRESS', 'ATTENTE'],
  },
  {
    tone: 'green',
    Icon: CheckCircle2,
    matches: ['APPROUV', 'APPROVED', 'VALID', 'TERMINE', 'PRESENT', 'COUVERTURE'],
  },
  {
    tone: 'red',
    Icon: XCircle,
    matches: ['REJET', 'REJECTED', 'REFUSED', 'ABSENT', 'ECHEC'],
  },
  {
    tone: 'orange',
    Icon: Users,
    matches: ['PARTICIP', 'PEOPLE', 'USERS', 'COLLAB', 'APPRENANT'],
  },
];

const formatDisplayValue = (value, formatType) => {
  if (value === null || value === undefined || value === '') return '0';
  if (formatType === 'PERCENT' || formatType === 'PERCENTAGE') {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? `${numeric}%` : String(value);
  }
  return String(value);
};

const resolveCompactVisual = (configuration) => {
  const raw = [
    configuration?.code,
    configuration?.name,
    configuration?.description,
    configuration?.fieldKey,
    configuration?.groupByFieldKey,
  ]
    .filter(Boolean)
    .join(' ')
    .toUpperCase();

  const match = keywordConfig.find((item) => item.matches.some((keyword) => raw.includes(keyword)));
  if (match) {
    return {
      tone: match.tone,
      Icon: match.Icon,
    };
  }

  return {
    tone: 'default',
    Icon: BarChart2,
  };
};

const KpiWidget = ({ configuration, data, isSimulated }) => {
  const { name, visualizationType, formatType, description } = configuration || {};
  const ChartIcon = chartIconByType[visualizationType] || BarChart2;
  const isCardWidget = visualizationType === 'CARD';
  const compactVisual = resolveCompactVisual(configuration);
  const compactTone = compactTones[compactVisual.tone] || compactTones.default;
  const CompactIcon = compactVisual.Icon;

  const renderCompactCard = () => (
    <Box className="kpi-widget__compact">
      <Box className="kpi-widget__compact-grid">
        <Box className="kpi-widget__compact-left">
          <Typography
            className="kpi-widget__compact-value"
            sx={{ color: compactTone.value }}
          >
            {formatDisplayValue(data?.value, formatType)}
          </Typography>

          <Box className="kpi-widget__compact-label">
            <TrendingUp size={13} style={{ color: compactTone.value, flexShrink: 0 }} />
            <span className="kpi-widget__compact-label-text">{name || 'KPI'}</span>
          </Box>
        </Box>

        <Box className="kpi-widget__compact-right">
          <Box
            className="kpi-widget__tone-badge"
            sx={{
              backgroundColor: compactTone.badgeBg,
              color: compactTone.badgeFg,
            }}
          >
            <CompactIcon size={18} />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={safeSeries(data?.series)} margin={{ top: 10, right: 8, left: -16, bottom: 4 }}>
        <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
        <Tooltip />
        <Bar dataKey="value" fill="#204385" radius={[10, 10, 0, 0]} name={name} />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderLineChart = () => (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={safeSeries(data?.series)} margin={{ top: 10, right: 8, left: -16, bottom: 4 }}>
        <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
        <Tooltip />
        <Line type="monotone" dataKey="value" stroke="#204385" strokeWidth={3} dot={{ r: 4, fill: '#ef4444' }} name={name} />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderPieChart = () => (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={safeSeries(data?.series)}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          outerRadius={84}
          innerRadius={38}
          paddingAngle={2}
          label
        >
          {safeSeries(data?.series).map((entry, index) => (
            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderTable = () => (
    <Table
      variant="plain"
      stickyHeader
      sx={{
        mt: 1,
        '--TableCell-headBackground': '#f8fafc',
        '--TableCell-paddingY': '10px',
        '--TableCell-paddingX': '12px',
        '& th': { color: '#475569', fontWeight: 700 },
        '& td': { color: '#0f172a' },
      }}
    >
      <thead>
        <tr>
          <th>Libelle</th>
          <th>Valeur</th>
        </tr>
      </thead>
      <tbody>
        {safeSeries(data?.series).map((row, index) => (
          <tr key={`${row?.label || 'row'}-${index}`}>
            <td>{row?.label || '-'}</td>
            <td>{row?.value ?? 0}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  const renderChartContent = () => {
    switch (visualizationType) {
      case 'BAR':
        return renderBarChart();
      case 'LINE':
        return renderLineChart();
      case 'PIE':
        return renderPieChart();
      case 'TABLE':
        return renderTable();
      default:
        return <Typography level="body-sm">Type de visualisation non supporte</Typography>;
    }
  };

  return (
    <Card
      variant="outlined"
      className={`kpi-widget ${isCardWidget ? 'kpi-widget--card' : 'kpi-widget--chart'} ${isSimulated ? 'kpi-widget--simulated' : ''}`}
      sx={{
        p: 0,
        overflow: 'hidden',
        minHeight: isCardWidget ? 70 : '100%',
      }}
    >
      {isSimulated && (
        <Box className="kpi-widget__simulation-badge">
          Simulation
        </Box>
      )}

      {isCardWidget ? (
        renderCompactCard()
      ) : (
        <Box className="kpi-widget__chart">
          <Box className="kpi-widget__chart-head">
            <Box className="kpi-widget__chart-meta">
              <Box
                className="kpi-widget__tone-badge"
                sx={{
                  backgroundColor: '#eff6ff',
                  color: '#204385',
                }}
              >
                <ChartIcon size={18} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography level="title-sm" sx={{ color: '#0f172a' }}>{name || 'KPI'}</Typography>
                <Typography level="body-xs" sx={{ color: '#64748b' }}>
                  {description || visualizationType || 'Widget'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ minHeight: 260 }}>
            {renderChartContent()}
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default KpiWidget;
